import type { PaletteOptions } from "@mui/material/styles";
import { navy, periwinkle, statusLight, statusDark, awaitingInput, brandGradient } from "@karda/design-tokens";

/**
 * Light palette: the historical brand identity, made explicit. primary
 * light/main/dark are the navy700/800/900 anchors, which also makes
 * palette.gradient.brand exactly the historical KARDA panel gradient
 * (#3d4260 → #26293B → #1a1c29).
 */
export const lightPalette: PaletteOptions = {
  mode: "light",
  primary: { main: navy[800], light: navy[700], dark: navy[900], contrastText: "#ffffff" },
  secondary: { main: "#D9D9D9" },
  ...statusLight,
  background: { default: "#ffffff", paper: "#ffffff" },
  // Keep neutral outlines visible on white and softly tinted surfaces.
  divider: "rgba(0, 0, 0, 0.45)",
  awaitingInput,
  gradient: {
    brand: brandGradient({ light: navy[700], main: navy[800], dark: navy[900] }),
    brandHover: brandGradient({ light: navy[600], main: navy[700], dark: navy[800] }),
    onBrand: "#ffffff",
  },
};

/**
 * Dark palette: derived from the brand navy (replaces the old "One Dark Pro"
 * editor theme). Surfaces sit on the navy ramp; the interactive accent is the
 * navy-hue periwinkle.
 *
 * WCAG AA contrast (≥4.5:1) on background.paper (navy900 #1a1c29):
 *   primary.main  #9ca4d3  6.98:1   text.primary (white .92)  ~16.9:1
 *   error #f44336 4.59:1   warning #ffa726 8.70:1
 *   info  #29b6f6 7.34:1   success #66bb6a 7.15:1
 */
export const darkPalette: PaletteOptions = {
  mode: "dark",
  primary: { ...periwinkle, contrastText: "rgba(0, 0, 0, 0.87)" },
  secondary: { main: navy[400] },
  ...statusDark,
  background: { default: navy[950], paper: navy[900] },
  text: {
    primary: "rgba(255, 255, 255, 0.92)",
    secondary: "rgba(255, 255, 255, 0.64)",
  },
  divider: "rgba(233, 234, 237, 0.12)", // alpha(navy100, 0.12)
  awaitingInput,
  gradient: {
    brand: brandGradient({ light: periwinkle.light, main: periwinkle.main, dark: periwinkle.dark }),
    brandHover: brandGradient({ light: "#cdd2ee", main: periwinkle.light, dark: periwinkle.main }),
    // The dark gradient is a LIGHT surface — content on it needs dark text.
    onBrand: "rgba(0, 0, 0, 0.87)",
  },
};
